import axios from 'axios';
import { logoutUser } from '../Context/authLogout';
import config from '../config';

// --- Axios instance with interceptors ---
const _axiosInstance = axios.create({
    baseURL: config.api.baseURL,
    timeout: config.api.timeout,
    headers: {
        'Content-Type': 'application/json'
    }
});

// --- Logout logic ---
const LOGOUT_FLAG = "LOGGED_OUT";
const isUserLoggedOut = () => localStorage.getItem(LOGOUT_FLAG) === "true";
const setLogoutFlag = () => localStorage.setItem(LOGOUT_FLAG, "true");
export const clearLogoutFlag = () => localStorage.removeItem("LOGGED_OUT");

_axiosInstance.interceptors.response.use(
    (response) => {
        checkSession(response?.data?.zmessage);
        return response;
    },
    (error) => {
        checkSession(error.response?.data?.zmessage);
        return Promise.reject(error);
    }
);

// Request interceptor
_axiosInstance.interceptors.request.use(
    (config) => {
        // Add auth token if available
        const token = localStorage.getItem('authToken');
        if (token) {
            config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
    },
    (error) => {
        return Promise.reject(error);
    }
);

function checkSession(zmessage) {
    if (Array.isArray(zmessage) && zmessage.length > 0) {
        const firstMessage = zmessage[0];
        if (
            firstMessage?.returnmessage === "Your Account no Longer Exists, Please contact Support for Assistance." &&
            firstMessage?.returncode === "0"
        ) {
            if (!isUserLoggedOut()) {
                setLogoutFlag();
                logoutUser();
            }
        }
    }
}

// --- Retry logic ---
const retryDelay = () => {
    // simple delay with some random jitter
    return 1000 + Math.random() * 1000;
};

_axiosInstance.interceptors.response.use(
    (res) => res,
    async (error) => {
        const originalRequest = error.config;

        // Retry only once
        if (!originalRequest._retried) {
            originalRequest._retried = true;

            // Retry only for network errors or server errors (status >= 500)
            if (!error.response || error.response.status >= 500) {
                await new Promise(resolve => setTimeout(resolve, retryDelay()));
                return _axiosInstance(originalRequest);
            }
        }
        return Promise.reject(error);
    }
);

// --- Queue + dedup ---
let requestQueue = [];
let isProcessingQueue = false;
let pendingRequests = new Map();

function generateRequestKey(method, url, data) {
    let key = `${method}:${url}`;
    if (data && (method === 'post' || method === 'put' || method === 'patch')) {
        try {
            key += `:${JSON.stringify(data)}`;
        } catch {
            key += ':unstringifiable-data';
        }
    }
    return key;
}

async function processQueue() {
    if (isProcessingQueue || requestQueue.length === 0) return;

    isProcessingQueue = true;
    const { method, url, data, config, resolve, reject, requestKey } = requestQueue.shift();

    try {
        let res;
        switch (method) {
            case 'get': res = await _axiosInstance.get(url, config); break;
            case 'post': res = await _axiosInstance.post(url, data, config); break;
            case 'put': res = await _axiosInstance.put(url, data, config); break;
            case 'delete': res = await _axiosInstance.delete(url, config); break;
            case 'patch': res = await _axiosInstance.patch(url, data, config); break;
            default: throw new Error(`Unsupported method: ${method}`);
        }
        resolve(res);
    } catch (err) {
        reject(err);
    } finally {
        isProcessingQueue = false;
        pendingRequests.delete(requestKey);
        processQueue();
    }
}

function enqueueRequest(method, url, data, config) {
    const requestKey = generateRequestKey(method, url, data);
    if (pendingRequests.has(requestKey)) {
        return pendingRequests.get(requestKey);
    }

    const promise = new Promise((resolve, reject) => {
        requestQueue.push({ method, url, data, config, resolve, reject, requestKey });
        processQueue();
    });

    pendingRequests.set(requestKey, promise);
    return promise;
}

// --- Final httpClient export ---
const httpClient = {
    get: (url, config) => enqueueRequest('get', url, undefined, config),
    post: (url, data, config) => enqueueRequest('post', url, data, config),
    put: (url, data, config) => enqueueRequest('put', url, data, config),
    delete: (url, config) => enqueueRequest('delete', url, undefined, config),
    patch: (url, data, config) => enqueueRequest('patch', url, data, config),
    interceptors: _axiosInstance.interceptors
};

export default httpClient;
